import { useForm } from "@tanstack/react-form";
import { useQuery } from "@tanstack/react-query";
import type { ChangeEvent, FocusEvent } from "react";
import { useEffect, useState } from "react";
import { Col, Container, Form, Row } from "react-bootstrap";
import { Trash } from "react-bootstrap-icons";
import { z } from "zod";
import {
  type Address,
  type BarLicense,
  type ContactInfo,
  ContactInfoCategory,
  ContactInfoType,
  Role,
} from "@/briefpoint-client";
import Button from "@/components/Button";
import FormFieldError from "@/components/FormFieldError";
import Help from "@/components/Help";
import { toast } from "@/components/ui/toast";
import { useJurisdictionApi } from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import type { FirmClient, UserResponse } from "@/lib/types";
import { getFieldError } from "@/lib/utils";
import Loading from "../Loading";
import styles from "./GeneralInformation.module.scss";

const barLicenseRowSchema = z.object({
  jurisdictionId: z.string().min(1, "Jurisdiction is required"),
  barNumber: z.string().trim().min(1, "Bar number is required"),
  id: z.string(),
});

function createPersonalInfoFormSchema(isFirmAdmin: boolean) {
  return z
    .object({
      firstName: z.string().trim().min(1, "First name is required"),
      lastName: z.string().trim().min(1, "Last name is required"),
      barNumber: z.string().trim().min(1, "Bar number is required"),
      barLicenses: z.array(barLicenseRowSchema),
      email: z
        .string()
        .trim()
        .min(1, "Contact email is required")
        .email("Invalid email address"),
      firmName: isFirmAdmin
        ? z.string().trim().min(1, "Firm name is required")
        : z.string(),
    })
    .superRefine((data, ctx) => {
      const licenses = data.barLicenses;

      const jurisdictionIndices = new Map<string, number[]>();
      for (let i = 0; i < licenses.length; i++) {
        const jId = licenses[i].jurisdictionId.trim();
        if (!jId) continue;
        const list = jurisdictionIndices.get(jId) ?? [];
        list.push(i);
        jurisdictionIndices.set(jId, list);
      }
      for (const indices of jurisdictionIndices.values()) {
        if (indices.length < 2) continue;
        for (const idx of indices) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Jurisdictions must be unique",
            path: ["barLicenses", idx, "jurisdictionId"],
          });
        }
      }

      const barNumberIndices = new Map<string, number[]>();
      for (let i = 0; i < licenses.length; i++) {
        const bn = licenses[i].barNumber.trim();
        if (!bn) continue;
        const key = bn.toLowerCase();
        const list = barNumberIndices.get(key) ?? [];
        list.push(i);
        barNumberIndices.set(key, list);
      }
      for (const indices of barNumberIndices.values()) {
        if (indices.length < 2) continue;
        for (const idx of indices) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Bar numbers must be unique",
            path: ["barLicenses", idx, "barNumber"],
          });
        }
      }

      const primaryBarKey = data.barNumber.trim().toLowerCase();
      if (primaryBarKey) {
        for (let i = 0; i < licenses.length; i++) {
          const bn = licenses[i].barNumber.trim();
          if (!bn) continue;
          if (bn.toLowerCase() === primaryBarKey) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Bar numbers must be unique",
              path: ["barLicenses", i, "barNumber"],
            });
          }
        }
      }
    });
}

type PersonalFormValues = z.infer<
  ReturnType<typeof createPersonalInfoFormSchema>
>;

function personalDefaultsFromAuth(
  user: UserResponse,
  firm: FirmClient,
): PersonalFormValues {
  return {
    firstName: user.firstName ?? "",
    lastName: user.lastName ?? "",
    barNumber: user.barNumber ?? "",
    barLicenses: (user.barLicenses ?? []).map((b) => ({
      jurisdictionId: b.jurisdictionId ?? "",
      barNumber: b.barNumber ?? "",
      id: (b as BarLicense & { id?: string }).id ?? "",
    })),
    email:
      user.contactInfo?.find((c) => c.type === ContactInfoType.Email)?.value ??
      "",
    firmName: firm.name ?? "",
  };
}

function mergeEmailIntoContactInfo(
  contacts: ContactInfo[] | undefined,
  email: string,
): ContactInfo[] {
  const base = contacts ?? [];
  const withoutEmail = base.filter((c) => c.type !== ContactInfoType.Email);
  return [
    ...withoutEmail,
    {
      value: email,
      type: ContactInfoType.Email,
      category: ContactInfoCategory.Office,
    },
  ];
}

interface GeneralInformationProps {
  documentId?: string;
  onSave?: () => void;
}

export default function GeneralInformationScreen(
  props: GeneralInformationProps,
) {
  const { user, updateUser, firm, updateFirm } = useAuth();
  const jurisdictionApi = useJurisdictionApi();

  if (!user) throw new Error("User is undefined");
  if (!firm) throw new Error("Firm is undefined");

  const [tmpFirm] = useState<FirmClient>(firm);
  const [tmpAddress, setTmpAddress] = useState<Address | undefined>(
    firm.address,
  );

  const jurisdictionsQuery = useQuery({
    queryKey: ["jurisdictionApi.jurisdictionGet"],
    queryFn: () => jurisdictionApi.jurisdictionGet(),
  });

  const [tmpPhone, setTmpPhone] = useState<ContactInfo>(
    (user?.role?.includes(Role.FirmAdmin) ? firm : user)?.contactInfo?.find(
      (c) => c.type === ContactInfoType.Phone,
    ) || {
      value: "",
      type: ContactInfoType.Phone,
      category: ContactInfoCategory.Office,
    },
  );
  const [tmpFax, setTmpFax] = useState<ContactInfo>(
    (user?.role?.includes(Role.FirmAdmin) ? firm : user)?.contactInfo?.find(
      (c) => c.type === ContactInfoType.Fax,
    ) || {
      value: "",
      type: ContactInfoType.Fax,
      category: ContactInfoCategory.Office,
    },
  );

  const initialHasCityOrState =
    firm?.address && !!(firm.address.city || firm.address.stateProvince);
  const isFirmAdmin = Boolean(user?.role?.includes(Role.FirmAdmin));

  const altPhone = !isFirmAdmin
    ? (firm?.contactInfo?.find((c) => c.type === ContactInfoType.Phone)
        ?.value ?? "")
    : "";
  const altFax = !isFirmAdmin
    ? (firm?.contactInfo?.find((c) => c.type === ContactInfoType.Fax)?.value ??
      "")
    : "";

  const form = useForm({
    defaultValues: personalDefaultsFromAuth(user, firm),
    validators: {
      onChange: createPersonalInfoFormSchema(isFirmAdmin),
    },
    onSubmit: async ({ value }) => {
      tmpFirm.address = tmpAddress;
      if (isFirmAdmin) {
        tmpFirm.name = value.firmName.trim();
      }

      const userContactInfo = mergeEmailIntoContactInfo(
        user.contactInfo?.filter(
          (c) =>
            c.type !== ContactInfoType.Phone && c.type !== ContactInfoType.Fax,
        ),
        value.email.trim(),
      );

      if (tmpPhone.value && !isFirmAdmin) {
        userContactInfo.push(tmpPhone);
      }
      if (tmpFax.value && !isFirmAdmin) {
        userContactInfo.push(tmpFax);
      }

      const userPayload: UserResponse = {
        ...user,
        firstName: value.firstName.trim(),
        lastName: value.lastName.trim(),
        barNumber: value.barNumber.trim(),
        barLicenses: value.barLicenses.map((row) => ({
          id: row.id,
          jurisdictionId: row.jurisdictionId,
          barNumber: row.barNumber.trim(),
        })) as BarLicense[],
        contactInfo: userContactInfo,
      };

      if (isFirmAdmin) {
        const firmContactInfo =
          firm.contactInfo?.filter(
            (c) =>
              c.type !== ContactInfoType.Phone &&
              c.type !== ContactInfoType.Fax,
          ) ?? [];
        if (tmpPhone.value) {
          firmContactInfo.push(tmpPhone);
        }
        if (tmpFax.value) {
          firmContactInfo.push(tmpFax);
        }
        tmpFirm.contactInfo = firmContactInfo;
      }

      await Promise.all([
        updateUser(userPayload),
        isFirmAdmin && updateFirm(tmpFirm),
      ]);
      if (props.onSave) {
        props.onSave();
      } else {
        toast.success("Information saved.");
      }
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset when auth identity changes, not on every reference update
  useEffect(() => {
    form.reset(personalDefaultsFromAuth(user, firm));
  }, [user.id, firm.id]);

  useEffect(() => {
    // Don't update the firm address outside of firm admins updating this page.
    if (firm && isFirmAdmin) {
      setTmpAddress(firm.address);
    }
  }, [firm, isFirmAdmin]);

  type FormControlElement = HTMLInputElement | HTMLTextAreaElement;

  // TODO: debounce onChange handler
  function handleOnUpdate<T>(
    event: ChangeEvent<FormControlElement> | FocusEvent<FormControlElement>,
    setter: (value: T) => void,
    obj: T,
    trim: boolean,
  ) {
    setter({
      ...obj,
      [event.target.name]: trim
        ? event.target.value.trim()
        : event.target.value,
    });
  }

  function handleAddressUpdate(
    event: ChangeEvent<FormControlElement> | FocusEvent<FormControlElement>,
    trim: boolean,
  ) {
    if (!tmpAddress) return;
    handleOnUpdate(event, setTmpAddress, tmpAddress, trim);
  }

  function checkUndefined(val: string | undefined | null): string {
    return val === undefined || val === null ? "" : val;
  }

  return (
    <Loading isLoading={jurisdictionsQuery.isLoading}>
      <Container className={styles.container}>
        <header className="mb-3">
          <div className={styles.header}>General Information</div>
          {props.documentId && (
            <p>
              Because this is your first time creating a document, we need to
              gather a few details.
            </p>
          )}
        </header>
        <Row>
          <Col xs="5">
            <div>
              <Form
                noValidate
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  void form.handleSubmit();
                }}
              >
                <h3 className="mb-3">Personal Info</h3>
                <Row className="gx-2">
                  <Col md>
                    <Form.Group className="mb-3">
                      <Form.Label htmlFor="first-name">First Name</Form.Label>
                      <form.Field
                        name="firstName"
                        children={(field) => (
                          <>
                            <Form.Control
                              id="first-name"
                              name="firstName"
                              value={field.state.value}
                              onChange={(e) =>
                                field.handleChange(e.target.value)
                              }
                              onBlur={() => {
                                field.handleChange(field.state.value.trim());
                                field.handleBlur();
                              }}
                              isInvalid={
                                field.state.meta.isTouched &&
                                !field.state.meta.isValid
                              }
                            />
                            <FormFieldError
                              errorMessage={getFieldError(field)}
                            />
                          </>
                        )}
                      />
                    </Form.Group>
                  </Col>
                  <Col md>
                    <Form.Group className="mb-3">
                      <Form.Label htmlFor="last-name">Last Name</Form.Label>
                      <form.Field
                        name="lastName"
                        children={(field) => (
                          <>
                            <Form.Control
                              id="last-name"
                              name="lastName"
                              value={field.state.value}
                              onChange={(e) =>
                                field.handleChange(e.target.value)
                              }
                              onBlur={() => {
                                field.handleChange(field.state.value.trim());
                                field.handleBlur();
                              }}
                              isInvalid={
                                field.state.meta.isTouched &&
                                !field.state.meta.isValid
                              }
                            />
                            <FormFieldError
                              errorMessage={getFieldError(field)}
                            />
                          </>
                        )}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="gx-2">
                  <Col md>
                    <Form.Group className="mb-2">
                      <Form.Label htmlFor="jurisdiction">
                        Jurisdiction
                      </Form.Label>
                      <Form.Select
                        name="jurisdiction"
                        id="jurisdiction"
                        value="default"
                        disabled
                      >
                        <option value="default">Default</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md>
                    <Form.Group className="mb-2">
                      <Form.Label htmlFor="bar-number">Bar Number</Form.Label>
                      <form.Field
                        name="barNumber"
                        children={(field) => (
                          <>
                            <Form.Control
                              id="bar-number"
                              name="barNumber"
                              value={field.state.value}
                              onChange={(e) =>
                                field.handleChange(e.target.value)
                              }
                              onBlur={() => {
                                field.handleChange(field.state.value.trim());
                                field.handleBlur();
                              }}
                              isInvalid={
                                field.state.meta.isTouched &&
                                !field.state.meta.isValid
                              }
                            />
                            <FormFieldError
                              errorMessage={getFieldError(field)}
                            />
                          </>
                        )}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <form.Field name="barLicenses" mode="array">
                  {(barLicensesField) => (
                    <>
                      {barLicensesField.state.value.map(
                        (barLicense, rowIndex) => (
                          <Row
                            key={barLicense.id || `row-${rowIndex}`}
                            className="gx-2 mb-2"
                          >
                            <Col md>
                              <Form.Group>
                                <form.Field
                                  name={`barLicenses[${rowIndex}].jurisdictionId`}
                                  children={(field) => (
                                    <>
                                      <Form.Select
                                        id={`bar-license-jurisdiction-${rowIndex}`}
                                        name="jurisdiction"
                                        value={field.state.value as string}
                                        onChange={(e) =>
                                          field.handleChange(e.target.value)
                                        }
                                        onBlur={() => field.handleBlur()}
                                        isInvalid={
                                          field.state.meta.isTouched &&
                                          !field.state.meta.isValid
                                        }
                                      >
                                        <option value="" disabled>
                                          Select a jurisdiction
                                        </option>
                                        {jurisdictionsQuery.data?.map((j) => (
                                          <option key={j.id} value={j.id ?? ""}>
                                            {j.name}
                                          </option>
                                        ))}
                                      </Form.Select>
                                      <FormFieldError
                                        errorMessage={getFieldError(field)}
                                      />
                                    </>
                                  )}
                                />
                              </Form.Group>
                            </Col>
                            <Col
                              md
                              className="d-flex ps-1 align-items-baseline"
                            >
                              <Form.Group className="w-100">
                                <form.Field
                                  name={`barLicenses[${rowIndex}].barNumber`}
                                  children={(field) => (
                                    <>
                                      <Form.Control
                                        value={field.state.value as string}
                                        onChange={(e) =>
                                          field.handleChange(e.target.value)
                                        }
                                        onBlur={() => {
                                          const v = field.state.value as string;
                                          field.handleChange(v.trim());
                                          field.handleBlur();
                                        }}
                                        isInvalid={
                                          field.state.meta.isTouched &&
                                          !field.state.meta.isValid
                                        }
                                      />
                                      <FormFieldError
                                        errorMessage={getFieldError(field)}
                                      />
                                    </>
                                  )}
                                />
                              </Form.Group>
                              <Button
                                variant="ghost"
                                type="button"
                                className={`m-0 ps-2 ${styles.trashButton}`}
                                onClick={() =>
                                  barLicensesField.removeValue(rowIndex)
                                }
                              >
                                <Trash />
                              </Button>
                            </Col>
                          </Row>
                        ),
                      )}
                      <Row className="mt-2">
                        <Col md className="d-flex justify-content-end">
                          <Button
                            variant="outline"
                            type="button"
                            onClick={() =>
                              barLicensesField.pushValue({
                                jurisdictionId: "",
                                barNumber: "",
                                id: "",
                              })
                            }
                          >
                            + Add Another Bar Number
                          </Button>
                        </Col>
                      </Row>
                    </>
                  )}
                </form.Field>
                <Row>
                  <Col xxl={12}>
                    <Form.Group className="mb-3">
                      <Form.Label htmlFor="contact-email">
                        Contact Email For Documents
                      </Form.Label>
                      <form.Field
                        name="email"
                        children={(field) => (
                          <>
                            <Form.Control
                              name="email"
                              id="contact-email"
                              type="email"
                              value={field.state.value}
                              onChange={(e) =>
                                field.handleChange(e.target.value)
                              }
                              onBlur={() => field.handleBlur()}
                              isInvalid={
                                field.state.meta.isTouched &&
                                !field.state.meta.isValid
                              }
                            />
                            <FormFieldError
                              errorMessage={getFieldError(field)}
                            />
                          </>
                        )}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <hr />
                <h3 className="mb-3">
                  Law Firm Info{" "}
                  {!isFirmAdmin && (
                    <Help
                      id="document-help"
                      text="Please contact your firm admin to change this information."
                      direction={"bottom"}
                    />
                  )}
                </h3>
                <Row>
                  <Col xxl={12}>
                    <Form.Group className="mb-3">
                      <Form.Label htmlFor="firm-name">Firm Name</Form.Label>
                      <form.Field
                        name="firmName"
                        children={(field) => (
                          <>
                            <Form.Control
                              id="firm-name"
                              name="firmName"
                              value={field.state.value}
                              onChange={(e) =>
                                field.handleChange(e.target.value)
                              }
                              onBlur={() => {
                                field.handleChange(field.state.value.trim());
                                field.handleBlur();
                              }}
                              disabled={!isFirmAdmin}
                              isInvalid={
                                field.state.meta.isTouched &&
                                !field.state.meta.isValid
                              }
                            />
                            <FormFieldError
                              errorMessage={getFieldError(field)}
                            />
                          </>
                        )}
                      />
                    </Form.Group>
                  </Col>
                  <Col xxl={12}>
                    <Form.Group className="mb-3">
                      <Form.Label htmlFor="street-address">
                        Street Address
                      </Form.Label>
                      <Form.Control
                        onBlur={(e) => handleAddressUpdate(e, true)}
                        onChange={(e) => handleAddressUpdate(e, false)}
                        name="street"
                        id="street-address"
                        value={checkUndefined(tmpAddress?.street)}
                        disabled={!isFirmAdmin}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col xxl={12}>
                    <Form.Group className="mb-3">
                      <Form.Label htmlFor="address-2">Address 2</Form.Label>
                      <Form.Control
                        placeholder="Suite, Bldg, Gate Code (optional)"
                        onBlur={(e) => handleAddressUpdate(e, true)}
                        onChange={(e) => handleAddressUpdate(e, false)}
                        name="street2"
                        disabled={!isFirmAdmin}
                        id="address-2"
                        value={checkUndefined(tmpAddress?.street2)}
                      />
                    </Form.Group>
                  </Col>
                  <Col xxl={12}>
                    <Form.Group className="mb-3">
                      <Row className="align-items-center">
                        <Col md={4}>
                          <Form.Label htmlFor="zip">Zip</Form.Label>
                          <Form.Control
                            onBlur={(e) => handleAddressUpdate(e, true)}
                            onChange={(e) => handleAddressUpdate(e, false)}
                            name="postalCode"
                            id="zip"
                            value={checkUndefined(tmpAddress?.postalCode)}
                            maxLength={5}
                            disabled={!isFirmAdmin}
                            required
                          />
                        </Col>
                        {initialHasCityOrState && (
                          <Col md={4}>
                            <Form.Label htmlFor="city">City</Form.Label>
                            <Form.Control
                              onBlur={(e) => handleAddressUpdate(e, true)}
                              onChange={(e) => handleAddressUpdate(e, false)}
                              name="city"
                              id="city"
                              value={checkUndefined(tmpAddress?.city)}
                              disabled={!isFirmAdmin}
                              required
                            />
                          </Col>
                        )}
                        {initialHasCityOrState && (
                          <Col md={4}>
                            <Form.Label htmlFor="state">State</Form.Label>
                            <Form.Control
                              onBlur={(e) => handleAddressUpdate(e, true)}
                              onChange={(e) => handleAddressUpdate(e, false)}
                              name="stateProvince"
                              id="state"
                              value={checkUndefined(tmpAddress?.stateProvince)}
                              disabled={!isFirmAdmin}
                              required
                            />
                          </Col>
                        )}
                      </Row>
                    </Form.Group>
                  </Col>
                  <Col xxl={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Phone Number (optional)</Form.Label>
                      <Form.Control
                        onBlur={(e) =>
                          handleOnUpdate(e, setTmpPhone, tmpPhone, true)
                        }
                        onChange={(e) =>
                          handleOnUpdate(e, setTmpPhone, tmpPhone, false)
                        }
                        name="value"
                        placeholder={altPhone}
                        value={checkUndefined(tmpPhone.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col xxl={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Fax Number (optional)</Form.Label>
                      <Form.Control
                        onBlur={(e) =>
                          handleOnUpdate(e, setTmpFax, tmpFax, true)
                        }
                        onChange={(e) =>
                          handleOnUpdate(e, setTmpFax, tmpFax, false)
                        }
                        name="value"
                        placeholder={altFax}
                        value={checkUndefined(tmpFax.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <div className="d-flex justify-content-end">
                  <form.Subscribe
                    selector={(state) => ({
                      isValid: state.isValid,
                    })}
                  >
                    {(formState) => (
                      <Button
                        type="submit"
                        loading={form.state.isSubmitting}
                        text="Saving..."
                        disabled={form.state.isSubmitting || !formState.isValid}
                      >
                        {props.documentId ? "Next" : "Save"}
                      </Button>
                    )}
                  </form.Subscribe>
                </div>
              </Form>
            </div>
          </Col>
        </Row>
      </Container>
    </Loading>
  );
}
